/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  EnhancedObjective,
  ObjectiveCategory,
  ObjectiveContext,
  ObjectiveProgress,
  ObjectiveNotification,
  ObjectivesState,
  ObjectiveCriteria,
  ObjectiveCondition,
  OBJECTIVE_CATEGORIES
} from '../types/enhancedObjectives';

export class EnhancedObjectivesManager {
  private state: ObjectivesState;
  private notificationCallback?: (notification: ObjectiveNotification) => void;

  constructor() {
    this.state = {
      categories: OBJECTIVE_CATEGORIES,
      objectives: {},
      progress: {},
      activeSets: {},
      completedSets: [],
      currentFocus: null,
      autoTrack: true,
      showHints: true,
      filterDifficulty: false,
      filterCategory: null,
      sortBy: 'importance',
      viewMode: 'tree'
    };
  }

  // State Management
  public getState(): ObjectivesState {
    return { ...this.state };
  }

  public setState(newState: Partial<ObjectivesState>): void {
    this.state = { ...this.state, ...newState };
  }

  // Objective Management
  public addObjective(objective: EnhancedObjective): void {
    this.state.objectives[objective.id] = { ...objective };
    this.updateObjectiveAvailability(objective.id);
  }

  public updateObjective(objectiveId: string, updates: Partial<EnhancedObjective>): void {
    if (this.state.objectives[objectiveId]) {
      this.state.objectives[objectiveId] = {
        ...this.state.objectives[objectiveId],
        ...updates
      };
    }
  }

  public getObjective(objectiveId: string): EnhancedObjective | undefined {
    return this.state.objectives[objectiveId];
  }

  public getObjectivesByCategory(categoryId: string): EnhancedObjective[] {
    return Object.values(this.state.objectives).filter(
      obj => obj.categoryId === categoryId && obj.status !== 'hidden'
    );
  }

  public getAvailableObjectives(): EnhancedObjective[] {
    return Object.values(this.state.objectives).filter(
      obj => obj.status === 'available'
    );
  }

  public getInProgressObjectives(): EnhancedObjective[] {
    return Object.values(this.state.objectives).filter(
      obj => obj.status === 'in_progress'
    );
  }

  // Objective Progress Evaluation
  public evaluateObjectives(context: ObjectiveContext): void {
    Object.keys(this.state.objectives).forEach(objectiveId => {
      const objective = this.state.objectives[objectiveId];

      if (objective.status === 'available' || objective.status === 'in_progress') {
        const evaluation = this.evaluateObjective(objective, context);
        this.updateObjectiveProgress(objectiveId, evaluation);
      }
    });
  }

  private evaluateObjective(objective: EnhancedObjective, context: ObjectiveContext): {
    progress: number;
    isCompleted: boolean;
    isFailed: boolean;
    message: string;
  } {
    const { criteria } = objective;
    let progress = 0;
    let isCompleted = false;
    let isFailed = false;
    let message = '';

    switch (criteria.type) {
      case 'threshold':
        progress = this.evaluateThresholdCriteria(criteria, context);
        isCompleted = progress >= objective.maxValue;
        break;

      case 'count':
        progress = this.evaluateCountCriteria(criteria, context);
        isCompleted = progress >= objective.maxValue;
        break;

      case 'combination':
        progress = this.evaluateCombinationCriteria(criteria, context);
        isCompleted = progress >= objective.maxValue;
        break;

      case 'sequence':
        progress = this.evaluateSequenceCriteria(criteria, context);
        isCompleted = progress >= objective.maxValue;
        break;

      case 'timed':
        const timedResult = this.evaluateTimedCriteria(criteria, context, objective);
        progress = timedResult.progress;
        isCompleted = timedResult.isCompleted;
        isFailed = timedResult.isFailed;
        break;
    }

    // Check failure conditions
    if (objective.failureConditions && !isCompleted) {
      const failureResult = this.checkFailureConditions(objective.failureConditions, context);
      if (failureResult.isFailed) {
        isFailed = true;
        message = failureResult.message;
      }
    }

    return { progress, isCompleted, isFailed, message };
  }

  private evaluateThresholdCriteria(criteria: ObjectiveCriteria, context: ObjectiveContext): number {
    switch (criteria.value) {
      case 'compliance':
        return context.currentStats.compliance;
      case 'budget':
        return context.currentStats.budget;
      case 'threat':
        return context.currentStats.threat;
      case 'day':
        return context.currentStats.day;
      default:
        return 0;
    }
  }

  private evaluateCountCriteria(criteria: ObjectiveCriteria, context: ObjectiveContext): number {
    if (typeof criteria.value === 'string' && context.buildings[criteria.value] !== undefined) {
      return context.buildings[criteria.value];
    }

    switch (criteria.value) {
      case 'products':
        return context.products.length;
      case 'research':
        return context.research.length;
      default:
        return 0;
    }
  }

  private evaluateCombinationCriteria(criteria: ObjectiveCriteria, context: ObjectiveContext): number {
    if (!criteria.conditions) return 0;

    let completedConditions = 0;
    criteria.conditions.forEach(condition => {
      if (this.evaluateCondition(condition, context)) {
        completedConditions++;
      }
    });

    return completedConditions;
  }

  private evaluateSequenceCriteria(criteria: ObjectiveCriteria, context: ObjectiveContext): number {
    // Complex sequence evaluation would go here
    // For now, treat as combination criteria
    return this.evaluateCombinationCriteria(criteria, context);
  }

  private evaluateTimedCriteria(
    criteria: ObjectiveCriteria,
    context: ObjectiveContext,
    objective: EnhancedObjective
  ): {
    progress: number;
    isCompleted: boolean;
    isFailed: boolean;
  } {
    const baseProgress = this.evaluateThresholdCriteria(criteria, context);
    const timeStarted = objective.timeStarted || context.currentStats.day;
    const timeElapsed = context.currentStats.day - timeStarted;

    if (objective.timeLimit && timeElapsed > objective.timeLimit) {
      return {
        progress: baseProgress,
        isCompleted: baseProgress >= objective.maxValue,
        isFailed: baseProgress < objective.maxValue
      };
    }

    return {
      progress: baseProgress,
      isCompleted: baseProgress >= objective.maxValue,
      isFailed: false
    };
  }

  private evaluateCondition(condition: ObjectiveCondition, context: ObjectiveContext): boolean {
    const statValue = this.getStatValue(condition.stat, context);

    switch (condition.operator) {
      case '>': return statValue > condition.value;
      case '<': return statValue < condition.value;
      case '>=': return statValue >= condition.value;
      case '<=': return statValue <= condition.value;
      case '==': return statValue === condition.value;
      default: return false;
    }
  }

  private getStatValue(stat: string, context: ObjectiveContext): number {
    switch (stat) {
      case 'budget': return context.currentStats.budget;
      case 'compliance': return context.currentStats.compliance;
      case 'threat': return context.currentStats.threat;
      case 'day': return context.currentStats.day;
      default:
        if (context.buildings[stat] !== undefined) {
          return context.buildings[stat];
        }
        return 0;
    }
  }

  private checkFailureConditions(conditions: ObjectiveCondition[], context: ObjectiveContext): {
    isFailed: boolean;
    message: string;
  } {
    for (const condition of conditions) {
      if (this.evaluateCondition(condition, context)) {
        return {
          isFailed: true,
          message: condition.description || 'Objective failed'
        };
      }
    }
    return { isFailed: false, message: '' };
  }

  private updateObjectiveProgress(objectiveId: string, evaluation: {
    progress: number;
    isCompleted: boolean;
    isFailed: boolean;
    message: string;
  }): void {
    const objective = this.state.objectives[objectiveId];
    const currentProgress = this.state.progress[objectiveId];

    // Initialize progress if not exists
    if (!currentProgress) {
      this.state.progress[objectiveId] = {
        objectiveId,
        currentProgress: 0,
        lastUpdated: Date.now(),
        milestones: { reached: 0, total: 10, values: [] },
        events: []
      };
    }

    const progress = this.state.progress[objectiveId];
    const oldProgress = progress.currentProgress;

    // Update progress
    progress.currentProgress = evaluation.progress;
    progress.lastUpdated = Date.now();

    // Check for status changes
    let statusChanged = false;
    let notification: ObjectiveNotification | null = null;

    if (objective.status === 'available' && evaluation.progress > 0) {
      objective.status = 'in_progress';
      objective.progress = evaluation.progress;
      objective.timeStarted = Date.now();
      statusChanged = true;

      notification = {
        type: 'progress',
        objectiveId,
        title: `Started: ${objective.title}`,
        message: `Progress: ${evaluation.progress}/${objective.maxValue}`,
        timestamp: Date.now(),
        severity: 'info'
      };
    } else if (objective.status === 'in_progress') {
      objective.progress = evaluation.progress;

      // Check for milestone (25% increments)
      const progressPercentage = (evaluation.progress / objective.maxValue) * 100;
      const milestonePercentage = Math.floor(progressPercentage / 25) * 25;

      if (milestonePercentage > 0 && milestonePercentage > (oldProgress / objective.maxValue) * 100) {
        notification = {
          type: 'milestone',
          objectiveId,
          title: `Milestone: ${objective.title}`,
          message: `${milestonePercentage}% complete!`,
          timestamp: Date.now(),
          severity: 'success'
        };
      }

      if (evaluation.isCompleted) {
        objective.status = 'completed';
        objective.progress = objective.maxValue;
        objective.timeCompleted = Date.now();
        statusChanged = true;

        notification = {
          type: 'completed',
          objectiveId,
          title: `Completed: ${objective.title}`,
          message: 'Great job! Objective completed successfully!',
          timestamp: Date.now(),
          severity: 'success'
        };
      } else if (evaluation.isFailed) {
        objective.status = 'failed';
        statusChanged = true;

        notification = {
          type: 'failed',
          objectiveId,
          title: `Failed: ${objective.title}`,
          message: evaluation.message,
          timestamp: Date.now(),
          severity: 'error'
        };
      }
    }

    // Update unlocked objectives
    if (statusChanged && objective.status === 'completed') {
      this.unlockDependentObjectives(objectiveId);
    }

    // Send notification
    if (notification && this.notificationCallback) {
      this.notificationCallback(notification);
    }
  }

  private updateObjectiveAvailability(objectiveId: string): void {
    const objective = this.state.objectives[objectiveId];

    if (objective.status === 'locked') {
      const prerequisitesMet = this.checkPrerequisites(objective.prerequisites);
      if (prerequisitesMet) {
        objective.status = 'available';

        if (this.notificationCallback) {
          this.notificationCallback({
            type: 'unlocked',
            objectiveId,
            title: `New Objective Available: ${objective.title}`,
            message: objective.description,
            timestamp: Date.now(),
            severity: 'info'
          });
        }
      }
    }
  }

  private checkPrerequisites(prerequisites: any[]): boolean {
    // Simple prerequisite checking - would need to be enhanced
    return prerequisites.length === 0; // For now, assume all prerequisites are met
  }

  private unlockDependentObjectives(completedObjectiveId: string): void {
    Object.values(this.state.objectives).forEach(objective => {
      if (objective.dependencies.includes(completedObjectiveId) && objective.status === 'locked') {
        this.updateObjectiveAvailability(objective.id);
      }
    });
  }

  // UI and Interaction
  public setObjectiveFocus(objectiveId: string | null): void {
    this.state.currentFocus = objectiveId;
  }

  public setAutoTrack(autoTrack: boolean): void {
    this.state.autoTrack = autoTrack;
  }

  public setShowHints(showHints: boolean): void {
    this.state.showHints = showHints;
  }

  public setFilterCategory(categoryId: string | null): void {
    this.state.filterCategory = categoryId;
  }

  public setSortBy(sortBy: ObjectivesState['sortBy']): void {
    this.state.sortBy = sortBy;
  }

  public setViewMode(viewMode: ObjectivesState['viewMode']): void {
    this.state.viewMode = viewMode;
  }

  // Notifications
  public setNotificationCallback(callback: (notification: ObjectiveNotification) => void): void {
    this.notificationCallback = callback;
  }

  // Utility Methods
  public getObjectivesWithProgress(): Array<{
    objective: EnhancedObjective;
    progress: ObjectiveProgress | null;
    progressPercentage: number;
  }> {
    return Object.values(this.state.objectives).map(objective => {
      const progress = this.state.progress[objective.id];
      const progressPercentage = objective.maxValue > 0
        ? (objective.progress / objective.maxValue) * 100
        : 0;

      return {
        objective,
        progress,
        progressPercentage
      };
    });
  }

  public getCategoryStats(): Array<{
    category: ObjectiveCategory;
    totalObjectives: number;
    completedObjectives: number;
    inProgressObjectives: number;
    availableObjectives: number;
    completionPercentage: number;
  }> {
    return this.state.categories.map(category => {
      const objectives = Object.values(this.state.objectives).filter(
        obj => obj.categoryId === category.id
      );

      const completed = objectives.filter(obj => obj.status === 'completed').length;
      const inProgress = objectives.filter(obj => obj.status === 'in_progress').length;
      const available = objectives.filter(obj => obj.status === 'available').length;

      return {
        category,
        totalObjectives: objectives.length,
        completedObjectives: completed,
        inProgressObjectives: inProgress,
        availableObjectives: available,
        completionPercentage: objectives.length > 0 ? (completed / objectives.length) * 100 : 0
      };
    });
  }

  public getRecommendedObjectives(limit: number = 5): EnhancedObjective[] {
    const available = this.getAvailableObjectives();

    // Sort by importance and difficulty, then return top recommendations
    return available
      .sort((a, b) => {
        const importanceWeight = { critical: 4, high: 3, medium: 2, low: 1 };
        const difficultyWeight = { trivial: 4, easy: 3, medium: 2, hard: 1, expert: 0.5 };

        const aScore = importanceWeight[a.importance] * difficultyWeight[a.difficulty];
        const bScore = importanceWeight[b.importance] * difficultyWeight[b.difficulty];

        return bScore - aScore;
      })
      .slice(0, limit);
  }
}

export const enhancedObjectivesManager = new EnhancedObjectivesManager();